// =============================================================================
// Peças comuns às duas grades do orçamento
//
// A Planilha orçamentária e o Orçado vs Realizado são duas tabelas largas, com
// centro de custo e categoria nas linhas e os meses nas colunas. O Financeiro
// pediu em 21/10, para "tudo que tem em relação à planilha orçamentária":
//
//   · cabeçalho congelado;
//   · ficar mais fácil passar para o lado;
//   · a coluna dos nomes com largura ajustável;
//   · abrir e fechar o centro de custo;
//   · os filtros do topo em lista suspensa.
//
// E, no mesmo dia, depois de ver a tela: a barra de filtros "ficou gigante" —
// ela é compacta e recolhe com um botão, lembrado por pessoa —, e as linhas sem
// valor no período nunca aparecem, sem caixa para mostrá-las.
//
// Ficam aqui, e não em cada tela, para as duas grades se comportarem igual.
//
// ⚠️ Cabeçalho congelado e "passar para o lado" eram o MESMO defeito. A tabela
// morava numa caixa com rolagem só horizontal (`overflow-x-auto`), e o
// navegador prende o `sticky` do cabeçalho à caixa de rolagem mais próxima — que
// não rolava na vertical, então o cabeçalho subia junto com a página. E a barra
// horizontal ficava no fim da tabela: com 400 linhas, era preciso descer até o
// fim para passar para o lado. A caixa agora tem a altura da tela e rola nos
// dois sentidos: o cabeçalho e a coluna dos nomes ficam presos, e a barra
// lateral fica sempre à vista.
// =============================================================================

import { useCallback, useRef, useState, type ReactNode } from "react";

export const LARGURA_NOME = 300;
const LARGURA_MIN = 180;
const LARGURA_MAX = 720;

/**
 * Largura da coluna dos nomes, lembrada no navegador de cada pessoa — conforto,
 * como as seções recolhidas da sidebar. Sem armazenamento (janela privada,
 * dado bloqueado), vale o padrão e a escolha dura só a visita.
 */
export function useLarguraColuna(chave: string, padrao = LARGURA_NOME) {
  const chaveArmazenada = `nexa.grade.${chave}.largura`;
  const [largura, setLargura] = useState<number>(() => {
    try {
      const v = Number(localStorage.getItem(chaveArmazenada));
      return v >= LARGURA_MIN && v <= LARGURA_MAX ? v : padrao;
    } catch {
      return padrao;
    }
  });
  const gravar = useCallback((v: number) => {
    try {
      localStorage.setItem(chaveArmazenada, String(v));
    } catch {
      /* sem armazenamento: vale só nesta visita */
    }
  }, [chaveArmazenada]);
  return { largura, setLargura, gravar, padrao };
}

/**
 * A alça na borda direita do cabeçalho dos nomes: arrastar muda a largura,
 * duplo clique volta ao padrão. Vai DENTRO do `<th>` (que é sticky, logo
 * posicionado: a alça se ancora nele).
 */
export function AlcaLargura({ coluna }: { coluna: ReturnType<typeof useLarguraColuna> }) {
  const inicio = useRef<{ x: number; largura: number } | null>(null);
  const limitar = (v: number) => Math.min(LARGURA_MAX, Math.max(LARGURA_MIN, Math.round(v)));
  return (
    <span
      role="separator"
      aria-orientation="vertical"
      aria-label="Largura da coluna dos nomes: arraste para ajustar, duplo clique volta ao padrão"
      title="Arraste para ajustar a largura · duplo clique volta ao padrão"
      onPointerDown={(e) => {
        e.preventDefault();
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
        inicio.current = { x: e.clientX, largura: coluna.largura };
      }}
      onPointerMove={(e) => {
        if (!inicio.current) return;
        coluna.setLargura(limitar(inicio.current.largura + e.clientX - inicio.current.x));
      }}
      onPointerUp={(e) => {
        if (!inicio.current) return;
        const v = limitar(inicio.current.largura + e.clientX - inicio.current.x);
        inicio.current = null;
        coluna.setLargura(v);
        coluna.gravar(v);
      }}
      onDoubleClick={() => {
        coluna.setLargura(coluna.padrao);
        coluna.gravar(coluna.padrao);
      }}
      className="absolute top-0 right-0 z-[1] h-full w-2 cursor-col-resize touch-none border-r-2 border-transparent hover:border-laranja/70"
    />
  );
}

/**
 * O conteúdo da coluna dos nomes com a largura escolhida. Largura no `<td>` não
 * prende uma tabela de layout automático — o texto longo esticaria a coluna —,
 * então quem fixa é este bloco, e o nome comprido é cortado com reticências.
 */
export function NomeNaLargura({ largura, children, className = "" }: {
  largura: number;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div style={{ width: largura }} className={`flex min-w-0 items-center gap-1.5 overflow-hidden ${className}`}>
      {children}
    </div>
  );
}

/**
 * A caixa da grade: altura da tela, rolagem nos dois sentidos. `reserva` tira
 * da altura o que fica fixo por cima dela no rodapé (a barra de "alterações
 * não salvas" da Planilha), senão a barra lateral ficaria escondida embaixo.
 */
export function CaixaGrade({ children, reserva = false }: { children: ReactNode; reserva?: boolean }) {
  return (
    <div
      className={`grade-scroll overflow-auto overscroll-contain rounded-lg border border-linha bg-superficie ${
        reserva ? "max-h-[calc(100vh-11.5rem)]" : "max-h-[calc(100vh-7rem)]"
      }`}
    >
      {children}
    </div>
  );
}

/** Grupos fechados da grade (o centro de custo, ou a categoria). Começam todos abertos. */
export function useRecolhidos() {
  const [fechados, setFechados] = useState<Set<string>>(new Set());
  const alternar = useCallback((chave: string) =>
    setFechados((a) => {
      const n = new Set(a);
      if (n.has(chave)) n.delete(chave); else n.add(chave);
      return n;
    }), []);
  return {
    fechado: (chave: string) => fechados.has(chave),
    alternar,
    fecharTodos: (chaves: string[]) => setFechados(new Set(chaves)),
    abrirTodos: () => setFechados(new Set()),
    algumFechado: fechados.size > 0,
  };
}

export function BotaoGrupo({ aberto, onClick, rotulo }: { aberto: boolean; onClick: () => void; rotulo: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={aberto}
      aria-label={`${aberto ? "Fechar" : "Abrir"} ${rotulo}`}
      title={aberto ? "Fechar" : "Abrir"}
      className="grid size-5 shrink-0 place-items-center rounded-md text-tinta-fraca hover:bg-superficie hover:text-tinta"
    >
      <span aria-hidden className={`text-[11px] transition-transform ${aberto ? "" : "-rotate-90"}`}>▾</span>
    </button>
  );
}

/** "Abrir todos / Fechar todos", ao lado dos filtros. */
export function AbrirFecharTodos({ grupos, recolhidos, rotulo }: {
  grupos: string[];
  recolhidos: ReturnType<typeof useRecolhidos>;
  rotulo: string;
}) {
  const todosFechados = grupos.length > 0 && grupos.every((g) => recolhidos.fechado(g));
  return (
    <button
      type="button"
      disabled={grupos.length === 0}
      onClick={() => (todosFechados ? recolhidos.abrirTodos() : recolhidos.fecharTodos(grupos))}
      className={CLASSE_BOTAO_BARRA}
    >
      {todosFechados ? `Abrir todos os ${rotulo}` : `Fechar todos os ${rotulo}`}
    </button>
  );
}

/**
 * A barra de filtros das grades: compacta, e recolhível. Recolhida, sobra uma
 * linha com o resumo do que está filtrado — esconder o filtro sem dizer qual
 * está valendo faria a pessoa ler a grade achando que vê tudo. As `acoes`
 * (exportar, editar, abrir e fechar os grupos) ficam à vista nos dois estados.
 */
export function FiltrosRecolhiveis({ chave, resumo, acoes, children }: {
  chave: string;
  resumo: string;
  acoes?: ReactNode;
  children: ReactNode;
}) {
  const chaveArmazenada = `nexa.grade.${chave}.filtros`;
  const [aberto, setAberto] = useState<boolean>(() => {
    try {
      return localStorage.getItem(chaveArmazenada) !== "fechado";
    } catch {
      return true;
    }
  });
  const alternar = () => setAberto((v) => {
    try {
      localStorage.setItem(chaveArmazenada, v ? "fechado" : "aberto");
    } catch {
      /* sem armazenamento: vale só nesta visita */
    }
    return !v;
  });
  return (
    <div className="surface-card mb-3 px-4 py-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <button
          type="button"
          onClick={alternar}
          aria-expanded={aberto}
          className="flex items-center gap-1.5 rounded-lg px-1.5 py-1 text-[12px] font-semibold text-tinta-media hover:bg-superficie-2"
        >
          <span aria-hidden className={`text-[10px] transition-transform ${aberto ? "" : "-rotate-90"}`}>▾</span>
          {aberto ? "Ocultar filtros" : "Mostrar filtros"}
        </button>
        {!aberto && <span className="min-w-0 truncate text-[12.5px] text-tinta-fraca" title={resumo}>{resumo}</span>}
        {acoes && <div className="ml-auto flex flex-wrap items-center gap-2">{acoes}</div>}
      </div>
      {aberto && <div className="mt-2.5 flex flex-wrap items-end gap-x-3 gap-y-2.5">{children}</div>}
    </div>
  );
}

/** O rótulo pequeno em cima de cada filtro da barra compacta. */
export function CampoCompacto({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold tracking-[0.04em] text-tinta-fraca uppercase">{rotulo}</span>
      {children}
    </label>
  );
}

/** Os botões da barra (exportar, abrir e fechar), no tamanho dela. */
export const CLASSE_BOTAO_BARRA =
  "rounded-lg border border-linha bg-superficie px-3 py-1.5 text-[12.5px] font-semibold text-tinta-media hover:border-linha-forte disabled:cursor-not-allowed disabled:opacity-45";

/** Filtro em lista suspensa (pedido de 21/10), no tamanho da barra compacta. */
export function Seletor<T extends string>({ valor, onMudar, opcoes, rotulo, desabilitado = false, title }: {
  valor: T;
  onMudar: (v: T) => void;
  opcoes: readonly (readonly [T, string])[];
  rotulo: string;
  desabilitado?: boolean;
  title?: string;
}) {
  return (
    <select
      value={valor}
      onChange={(e) => onMudar(e.target.value as T)}
      aria-label={rotulo}
      title={title}
      disabled={desabilitado}
      className="rounded-lg border border-linha bg-superficie px-2.5 py-1.5 text-[12.5px] disabled:bg-superficie-2 disabled:text-tinta-media"
    >
      {opcoes.map(([v, r]) => <option key={v} value={v}>{r}</option>)}
    </select>
  );
}
